<template>
  <div class="f-v f-ast">
    <donut-modal-sections-title :title="title" />
    <div class="f-as w-100p">
      <div class="t fc-g1 w-0 m-r4">{{ announcementDesc }}</div>
      <donut-selector-prefabs-channel
        v-model="channels"
        class="fc-g1 w-0 m-r4"
        :is-multi="true"
        :include-private="true"
        form-name="activation[announcement_channels][]"
      />
    </div>
    <div class="f-as m-tx2 w-100p">
      <div class="fc-g1 w-0 m-r4">
        <donut-input-text
          v-model="announcement"
          class="message-input"
          name="activation[announcement_text]"
          :autogrow="true"
          :autocomplete="['emoji', 'users', 'channels']"
          :textarea="true"
          :max-length="2500"
        />
      </div>
      <donut-slack-preview class="fc-g1 w-0 m-l4" :message="announcement">
        <div class="block">
          <cdn-img class="w-max-90p h-max-300 br-4" path="graphics/shoutouts/announcement" extension="gif" />
        </div>
        <div class="block">
          <div class="actions">
            <div v-for="(button, i) in announcementButtons" :key="`button-${i}`" v-emoji="button" class="action dark unpressable" />
          </div>
        </div>
      </donut-slack-preview>
    </div>
    <donut-modal-sections-actions
      button-text="Send message"
      :is-save-disabled="announcementIsInvalid"
      :loading-on-save="true"
      save-action="shoutoutsProgram/announceProgram"
      :save-args="saveArgs"
      :save-tracking="saveTracking"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import { compact } from 'lodash';
  import useStringFormatting from 'composables/string-formatting';

  const { state } = useStore();
  const $i18n = inject('$i18n');
  const { toSentence } = useStringFormatting();

  const title = computed(() => `Announce ${$i18n.t('shoutouts.feature')}`);
  const program = computed(() => state.shoutoutsProgram.program);

  // Announcement
  const announcementDesc = computed(() => `We recommend announcing ${$i18n.t('shoutouts.shoutout', { count: 2 })} in any channel`
      + ` you expect to see ${$i18n.t('shoutouts.shoutout', { count: 2 })}!`);

  const tags = state.modal.data?.tags;
  const valuesText = tags?.length > 0
      ? (`4. Add a core team value with a value emoji or hashtag (${tags.map(t => t.reverse_title).join(', ')})`)
      : '';

  const channelsPhrase = toSentence(program.value.channels.map(c => `<#${c.uid}>`), 'or');
  const emoji = `:${program.value.emoji}:`;
  const pointsName = { one: program.value.points_name, multi: program.value.points_name_plural };
  const renewalPeriodNoun = { weekly: 'week', monthly: 'month' }[program.value.renewal_period]; // This value is hardcoded in the model, but the user might have changed it in the form.
  const defaultAnnouncement = `:mega: Your team just launched *Donut ${$i18n.t('shoutouts.feature')}*: a simple way to give and receive `
    + `recognition ${$i18n.t('shoutouts.point', { count: 2 })} in Slack (and redeem them for real rewards).\n\nIt's easy:\n`
    + `1. Write a message in any channel where your team uses Donut (like ${channelsPhrase})\n`
    + '2. @ mention anyone you want to recognize\n'
    + `3. Add an emoji for each ${pointsName.one} ${emoji} you're giving or use a multiplier like ${emoji}x3 to send a specific amount\n`
    + `${valuesText}\n`
    + `\nEveryone has ${program.value.member_allowance} ${pointsName.multi} ${emoji} to give to others each ${renewalPeriodNoun}. `
    + 'Use \'em or lose \'em — they reset every Sunday.\n\n'
    + `The ${pointsName.multi} ${emoji} you receive from others *never expire* and can be redeemed at any time.`;
  const announcement = ref(defaultAnnouncement);
  const announcementButtons = computed(() => compact([
      `${$i18n.t('shoutouts.profile_emoji')} ${_.startCase($i18n.t('shoutouts.profile', { whose: 'My' }))}`,
      ...(program.value.rewards_enabled ? [`:${program.value.emoji}: Redeem`] : []),
      `:mailbox: DM a ${_.startCase($i18n.t('shoutouts.shoutout', { count: 1 }))}`,
      `${$i18n.t('shoutouts.how_to_emoji')} How To`,
    ]));

  // Form Submission
  const channels = ref(state.modal.data?.channels || []);
  const announcementIsInvalid = computed(() => !channels.value.length || !announcement.value.trim().length);
  const saveArgs = computed(() => ({
      channels: channels.value.map(c => c.id),
      text: announcement.value,
    }));
  const saveTracking = computed(() => {
    const madeAnnouncementChanges = announcement.value !== defaultAnnouncement;
    return {
      eventName: 'Shoutouts - Announcement (FE)',
      properties: {
        madeAnnouncementChanges,
        announcementText: madeAnnouncementChanges ? announcement.value : null,
        numAnnouncementChannels: channels.value.length,
      },
    };
  });
</script>
